/* globals window */
import { injectScript } from 'app/utils/env';
import { List } from 'immutable';
import appConfig from 'app/config/client';
import { IAnalyticsInstanceApiProps } from 'app/services/analytics/AnalyticsService';

interface IConfigInterfaceProps {
  trackingIds: List<string>
}

/* eslint-disable @typescript-eslint/naming-convention */
declare global {
  interface Window {
    dataLayer: unknown[];
  }
}
/* eslint-enable @typescript-eslint/naming-convention */

export const GA4_ALL_SITE_GOOGLE_ANALYTICS_ID = appConfig.environment.ga4AllSiteGoogleAnalyticsId;
const GTAG_ANALYTICS_SCRIPT_URL_PREFIX = 'https://www.googletagmanager.com/gtag/js?id=';
export const GTAG_ANALYTICS_SCRIPT_URL = GTAG_ANALYTICS_SCRIPT_URL_PREFIX + GA4_ALL_SITE_GOOGLE_ANALYTICS_ID;
const DMM_GOOGLE_ANALYTICS_ID = 'UA-48257133-1';

/**
 * `getGtagAnalyticsInitScript` returns a string to be injected into
 * the browser update pages. Thus, no ES6 code should be used here.
 */
// eslint-disable-next-line max-len
export const getGtagAnalyticsInitScript = (analyticsId: string[]) => `(function setGtagAnalyticsInitScript(brandAnalyticsIds) {
  window.dataLayer = window.dataLayer || [];

  function gtag() {
    window.dataLayer.push(arguments);
  }

  gtag('js', new Date());

  brandAnalyticsIds.forEach(function (id) {
    gtag('config', id);
  });
})(${JSON.stringify(analyticsId)})`;

/**
 * Install the global site tag (`gtag.js`) and ensure `gtag.js` is loaded only once.
 * You only need one global snippet per page, even for configuring multiple
 * Google Analytics properties
 */
const setUpWindowGtag = () => {
  window.dataLayer = window.dataLayer || [];

  function gtagHelper(method: 'js', date: Date): void;
  function gtagHelper(
    method: 'set' | 'config' | 'event',
    data: string,
    config?: { [key: string]: unknown }
  ): void;
  function gtagHelper() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  }

  gtagHelper('js', new Date());

  return gtagHelper;
};

let gtag: ReturnType<typeof setUpWindowGtag> | null;

const getGtag = () => {
  if (gtag) {
    return gtag;
  }

  injectScript(GTAG_ANALYTICS_SCRIPT_URL);

  const gtagHelper = setUpWindowGtag();
  gtag = gtagHelper;

  return gtagHelper;
};

const GoogleGtagAnalytics: (configProps: IConfigInterfaceProps) => IAnalyticsInstanceApiProps = (configProps) => {
  const { trackingIds } = configProps;

  if (trackingIds.size === 0) throw new Error('No GA trackingId defined');

  /**
   * Set values that will be sent with every event for a web page and configure properties
   */
  const identifyUser = (
    id: string | undefined,
    properties: {[key: string]: string} | undefined,
  ) => {
    const gtagHelper = getGtag();

    // Set user properties
    if (properties) {
      gtagHelper('set', 'user_properties', properties);
    }

    // You need to config the trackingId every time after you set new properties at the user level
    trackingIds.forEach((trackingId) => {
      if (trackingId === DMM_GOOGLE_ANALYTICS_ID) {
        // Always ignore the dmm id as DMM’s tracker is using DMM’s own user id (via DMM’s GTM)
        gtagHelper('config', trackingId);
      } else {
        gtagHelper('config', trackingId, {
          // when userId is undefined, it is considered unset for gtag
          user_id: id,
          // set `send_page_view` to false as default. We are defining our own page_path`
          // page data might be sent twice without turning off the default setting
          send_page_view: false,
        });
      }
    });
  };

  /**
   * Set listener to send pageview to track user's page visit
   */
  const trackPage = (page: string) => {
    const gtagHelper = getGtag();

    trackingIds.forEach((trackingId) => {
      gtagHelper('event', 'page_view', {
        page_path: page,
        send_to: trackingId,
      });
    });
  };

  return {
    identifyUser,
    trackPage,
  };
};

export default GoogleGtagAnalytics;
